import React from 'react';
import { Link } from 'react-router-dom';
import { Star, BadgeCheck, ExternalLink, MessageSquare } from 'lucide-react';

// ── Star row ───────────────────────────────────────────────────────────────
function Stars({ value, size = 'w-3.5 h-3.5' }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value >= n - 0.25;
        const half = !filled && value >= n - 0.75;
        return (
          <Star
            key={n}
            className={`${size} ${filled ? 'text-primary fill-primary' : half ? 'text-primary fill-primary/50' : 'text-white/15'}`}
          />
        );
      })}
    </div>
  );
}

// ── Compact badge for the buy box ──────────────────────────────────────────
// Shown inline near the title/price so the rating is visible at the decision point.
export function ProductRatingBadge({ reviews = [], avgRating = 0, ratingCount = 0 }) {
  if (ratingCount === 0) return null;
  const verified = reviews.filter((r) => r.verified).length;
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <Stars value={avgRating} />
      <span className="text-white font-bold text-sm">{avgRating.toFixed(1)}</span>
      <span className="text-white/40 text-xs">·</span>
      <span className="text-white/50 text-xs">{ratingCount} review{ratingCount === 1 ? '' : 's'}</span>
      {verified > 0 && (
        <span className="hidden sm:inline-flex items-center gap-1 text-primary/80 text-[10px] font-bold tracking-wider">
          <BadgeCheck className="w-3 h-3" /> {verified} VERIFIED
        </span>
      )}
    </div>
  );
}

// ── Full reviews section ────────────────────────────────────────────────────
// Rendered below the product detail. Only shows when the product has approved
// reviews. Each review displays the author, star rating, date, verified tag,
// source link (Google / Trustpilot / TripAdvisor / on-site) and the quote.
export default function ProductReviews({ reviews = [], avgRating = 0, ratingCount = 0, productName }) {
  if (ratingCount === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 border-t border-white/5">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">CUSTOMER REVIEWS</p>
          <h2 className="text-white font-black text-2xl md:text-3xl uppercase leading-tight">
            What Drivers Say
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="flex items-center gap-2 justify-end">
              <span className="text-white font-black text-2xl">{avgRating.toFixed(1)}</span>
              <Stars value={avgRating} size="w-4 h-4" />
            </div>
            <p className="text-white/40 text-[11px] mt-1">Based on {ratingCount} review{ratingCount === 1 ? '' : 's'}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
        {reviews.map((r) => {
          const sourceLabel = r.source_label || (r.source && r.source !== 'customer' ? r.source : 'Verified Customer');
          const isExternal = r.source && r.source !== 'customer' && !!r.source_url;
          return (
            <div key={r.id} className="bg-[#111115] p-6 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Stars value={Number(r.rating) || 0} />
                  {r.verified && (
                    <span className="inline-flex items-center gap-1 text-primary/80 text-[9px] font-bold tracking-wider">
                      <BadgeCheck className="w-3 h-3" /> VERIFIED
                    </span>
                  )}
                </div>
                {isExternal ? (
                  <a
                    href={r.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-white/40 hover:text-primary text-[10px] font-bold tracking-wider transition-colors"
                  >
                    {sourceLabel} <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                ) : (
                  <span className="text-white/30 text-[10px] font-bold tracking-wider">{sourceLabel}</span>
                )}
              </div>
              <p className="text-white/70 text-sm leading-relaxed flex-1 mb-4">
                &ldquo;{r.quote}&rdquo;
              </p>
              <div className="flex items-center justify-between">
                <p className="text-white font-bold text-xs">{r.author_name || 'Customer'}</p>
                {r.author_location && <p className="text-white/30 text-[10px]">{r.author_location}</p>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 text-center">
        <Link
          to="/review"
          className="inline-flex items-center gap-2 border border-white/10 hover:border-primary/40 text-white/50 hover:text-primary text-xs font-bold tracking-widest px-5 py-2.5 transition-colors"
        >
          <MessageSquare className="w-3.5 h-3.5" /> WRITE A REVIEW
        </Link>
      </div>
    </section>
  );
}